import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sparkles, AlertTriangle, ChevronLeft, FileText, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  client_type: "person",
  first_name: "", last_name: "", company_name: "",
  email: "", phone: "",
  address: "", postal_code: "", city: "", canton: "", country: "CH",
  date_of_birth: "", place_of_birth: "",
  nationality: "", permit_type: "", preferred_language: "",
  civil_status: "", housing_situation: "",
  ahv_number: "", employer: "", notes: "",
};

const PLACEHOLDER = `Beispiel — fügen Sie hier eine E-Mail-Signatur, eine vCard, einen WhatsApp-Text oder eine Visitenkarte ein:

Abreu de Sousa Rafael
Rheinsfelderstrasse 33, 8193 Eglisau (ZH)
Sprache: Deutsch
Geboren: 30.03.1992
Nationalität: Portugal
Ausländerausweis B
Wohnsituation: Mieter
Zivilstand: ledig
Mobile: +41 76 744 25 16
Email: rafasousa10@gmail.com`;

const CANTONS = ["AG", "AI", "AR", "BE", "BL", "BS", "FR", "GE", "GL", "GR", "JU", "LU", "NE", "NW", "OW", "SG", "SH", "SO", "SZ", "TG", "TI", "UR", "VD", "VS", "ZG", "ZH"];

export default function ImportContactFromTextDialog({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const [step, setStep] = useState("input"); // "input" | "preview"
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [duplicates, setDuplicates] = useState([]);

  const reset = () => {
    setStep("input");
    setText("");
    setForm(EMPTY);
    setDuplicates([]);
    setBusy(false);
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const extractFromText = async () => {
    if (!text.trim()) {
      toast.error("Bitte Text einfügen");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/clients/extract-from-text", { text });
      const { extracted, duplicates: dups } = res.data;
      setForm({ ...EMPTY, ...extracted });
      setDuplicates(dups || []);
      setStep("preview");
      toast.success("Daten erkannt — bitte überprüfen");
    } catch (e) {
      const msg = e.response?.data?.detail || "AI-Extraktion fehlgeschlagen";
      if (msg.toLowerCase().includes("budget")) {
        toast.error("LLM-Guthaben aufgebraucht — bitte aufladen (Profil → Universal Key)");
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const saveClient = async () => {
    if (!form.first_name && !form.last_name && !form.company_name) {
      toast.error("Mindestens Vorname/Nachname oder Firmenname erforderlich");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        client_type: form.client_type || "person",
        first_name: form.first_name || "",
        last_name: form.last_name || "",
        company_name: form.company_name || "",
        email: form.email || "",
        phone: form.phone || "",
        address: form.address || "",
        postal_code: form.postal_code || "",
        city: form.city || "",
        canton: form.canton || "",
        country: form.country || "CH",
        date_of_birth: form.date_of_birth || null,
        place_of_birth: form.place_of_birth || "",
        nationality: form.nationality || "",
        permit_type: form.permit_type || "",
        preferred_language: form.preferred_language || "",
        civil_status: form.civil_status || "",
        housing_situation: form.housing_situation || "",
        ahv_number: form.ahv_number || "",
        employer: form.employer || "",
        notes: form.notes || "",
        // Backend requires these two fields — default to "Import" when the
        // contact comes from AI text extraction (consultant can edit later).
        quelle: "Import (Text)",
        erstkontakt_durch: user?.full_name || "Import",
      };
      const res = await api.post("/clients", payload);
      toast.success("Kunde angelegt");
      onCreated?.(res.data);
      handleClose(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="import-text-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-prime" />
            {step === "input" ? "Aus Text importieren" : "Erkannte Daten überprüfen"}
          </DialogTitle>
        </DialogHeader>

        {step === "input" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Fügen Sie einen beliebigen Text ein (E-Mail-Signatur, vCard, WhatsApp-Nachricht, Visitenkarte…).
              Die KI extrahiert automatisch Name, Telefon, E-Mail, Adresse und mehr.
            </p>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={PLACEHOLDER}
              rows={12}
              className="font-mono text-xs"
              data-testid="import-text-input"
              autoFocus
            />
            <div className="text-xs text-muted-foreground">
              Zeichen: <span className="font-mono">{text.length} / 8000</span>
            </div>
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-4">
            {duplicates.length > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-md p-3 flex gap-2" data-testid="dup-warning">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-amber-800">
                    {duplicates.length} mögliche{duplicates.length === 1 ? "r" : ""} Duplikat{duplicates.length === 1 ? "" : "e"} gefunden
                  </div>
                  <ul className="text-xs text-amber-700 mt-1 space-y-0.5">
                    {duplicates.map((d, i) => (
                      <li key={i} className="font-mono">
                        · {d.first_name} {d.last_name}
                        {d.company_name ? ` (${d.company_name})` : ""}
                        {d.email ? ` — ${d.email}` : ""}
                        {d.phone ? ` — ${d.phone}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-uppercase text-[10px]">Typ</Label>
                <Select value={form.client_type} onValueChange={(v) => set("client_type", v)}>
                  <SelectTrigger data-testid="form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="person">Person</SelectItem>
                    <SelectItem value="business">Unternehmen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.client_type === "business" ? (
                <div>
                  <Label className="label-uppercase text-[10px]">Firmenname</Label>
                  <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="form-company" />
                </div>
              ) : (
                <div /> /* placeholder col */
              )}
              <div>
                <Label className="label-uppercase text-[10px]">Vorname</Label>
                <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="form-first-name" />
              </div>
              <div>
                <Label className="label-uppercase text-[10px]">Nachname</Label>
                <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="form-last-name" />
              </div>
              <div className="col-span-2">
                <Label className="label-uppercase text-[10px]">E-Mail</Label>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="form-email" />
              </div>
              <div className="col-span-2">
                <Label className="label-uppercase text-[10px]">Telefon / Handy</Label>
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+41 79 ..." data-testid="form-phone" />
              </div>
              <div className="col-span-2">
                <Label className="label-uppercase text-[10px]">Strasse + Nr.</Label>
                <Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="form-address" />
              </div>
              <div>
                <Label className="label-uppercase text-[10px]">PLZ</Label>
                <Input value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} data-testid="form-postal" />
              </div>
              <div>
                <Label className="label-uppercase text-[10px]">Ort</Label>
                <Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="form-city" />
              </div>
              <div>
                <Label className="label-uppercase text-[10px]">Kanton</Label>
                <Select value={form.canton || "__none__"} onValueChange={(v) => set("canton", v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="form-canton"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none__">—</SelectItem>
                    {CANTONS.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-uppercase text-[10px]">Land</Label>
                <Input value={form.country} onChange={(e) => set("country", e.target.value.toUpperCase())} maxLength={2} data-testid="form-country" />
              </div>
              <div>
                <Label className="label-uppercase text-[10px]">Geb. Datum</Label>
                <Input type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} data-testid="form-dob" />
              </div>
              <div>
                <Label className="label-uppercase text-[10px]">Geburtsort</Label>
                <Input value={form.place_of_birth} onChange={(e) => set("place_of_birth", e.target.value)} data-testid="form-pob" />
              </div>
              <div>
                <Label className="label-uppercase text-[10px]">Nationalität</Label>
                <Input value={form.nationality} onChange={(e) => set("nationality", e.target.value)} placeholder="z. B. Portugal" data-testid="form-nationality" />
              </div>
              <div>
                <Label className="label-uppercase text-[10px]">Sprache</Label>
                <Select value={form.preferred_language || "__none__"} onValueChange={(v) => set("preferred_language", v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="form-language"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="it">Italiano</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-uppercase text-[10px]">Ausweis</Label>
                <Select value={form.permit_type || "__none__"} onValueChange={(v) => set("permit_type", v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="form-permit"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    <SelectItem value="B">Ausländerausweis B</SelectItem>
                    <SelectItem value="L">Ausländerausweis L</SelectItem>
                    <SelectItem value="C">Ausländerausweis C</SelectItem>
                    <SelectItem value="Swisspass">Swisspass</SelectItem>
                    <SelectItem value="Meldebestätigung">Meldebestätigung</SelectItem>
                    <SelectItem value="CartaIdentita">{`Carta d'identità`}</SelectItem>
                    <SelectItem value="Patente">Patente</SelectItem>
                    <SelectItem value="Passaporto">Passaporto</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-uppercase text-[10px]">Zivilstand</Label>
                <Select value={form.civil_status || "__none__"} onValueChange={(v) => set("civil_status", v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="form-civil-status"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    <SelectItem value="celibe">Ledig</SelectItem>
                    <SelectItem value="sposato">Verheiratet</SelectItem>
                    <SelectItem value="separato">Getrennt</SelectItem>
                    <SelectItem value="divorziato">Geschieden</SelectItem>
                    <SelectItem value="vedovo">Verwitwet</SelectItem>
                    <SelectItem value="orfano">Waise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-uppercase text-[10px]">Wohnsituation</Label>
                <Select value={form.housing_situation || "__none__"} onValueChange={(v) => set("housing_situation", v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="form-housing"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    <SelectItem value="mieter">Mieter</SelectItem>
                    <SelectItem value="eigentuemer">Eigentümer</SelectItem>
                    <SelectItem value="bei_eltern">Bei Eltern</SelectItem>
                    <SelectItem value="wg">WG</SelectItem>
                    <SelectItem value="altro">Andere</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-uppercase text-[10px]">AHV-Nr.</Label>
                <Input value={form.ahv_number} onChange={(e) => set("ahv_number", e.target.value)} placeholder="756.xxxx.xxxx.xx" data-testid="form-ahv" />
              </div>
              <div>
                <Label className="label-uppercase text-[10px]">Arbeitgeber</Label>
                <Input value={form.employer} onChange={(e) => set("employer", e.target.value)} data-testid="form-employer" />
              </div>
              <div className="col-span-2">
                <Label className="label-uppercase text-[10px]">Notizen</Label>
                <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="form-notes" />
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex justify-between gap-2">
          {step === "preview" ? (
            <Button variant="outline" onClick={() => setStep("input")} disabled={busy} data-testid="back-to-input-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
            </Button>
          ) : <div />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleClose(false)} disabled={busy} data-testid="cancel-btn">
              Abbrechen
            </Button>
            {step === "input" ? (
              <Button onClick={extractFromText} disabled={busy || !text.trim()} className="btn-prime" data-testid="extract-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                KI-Erkennung starten
              </Button>
            ) : (
              <Button onClick={saveClient} disabled={busy} className="btn-prime" data-testid="save-imported-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                Kunde anlegen
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
